// Customer Story — sticky sidebar (Figma 12250:40045).
// Scroll-nav plus an "Apply" CTA. The whole column is sticky and pins beside
// the (taller) article. Hidden below lg, where the mobile scroll bar takes over.

import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import type { CaseStudyStory } from "@/lib/content/schemas";

import { CustomerStorySidebarNav } from "./sidebar-nav";
import type { StoryNavItem } from "./use-active-section";

const SIGNUP_URL = "https://app.wayflyer.com/authn/signup";

interface CustomerStorySidebarProps {
  navItems: StoryNavItem[];
  navigationAriaLabel?: string;
  apply?: CaseStudyStory["apply"];
  defaultApplyText?: string;
  defaultApplyLabel?: string;
}

export function CustomerStorySidebar({
  navItems,
  navigationAriaLabel,
  apply,
  defaultApplyText,
  defaultApplyLabel,
}: CustomerStorySidebarProps) {
  return (
    <aside className="border-grey-100 bg-grey-0 hidden w-[196px] shrink-0 border-b border-l lg:block">
      <div className="bg-grey-0 sticky top-[calc(var(--nav-offset)+16px)] flex flex-col self-start">
        <CustomerStorySidebarNav
          items={navItems}
          ariaLabel={navigationAriaLabel}
        />
        <ApplyCard
          apply={apply}
          defaultText={defaultApplyText}
          defaultLabel={defaultApplyLabel}
        />
      </div>
    </aside>
  );
}

function ApplyCard({
  apply,
  defaultText = "Apply in less than 10 minutes.",
  defaultLabel = "Apply now",
}: {
  apply?: CaseStudyStory["apply"];
  defaultText?: string;
  defaultLabel?: string;
}) {
  const text = apply?.text ?? defaultText;
  const label = apply?.label ?? defaultLabel;
  const href = apply?.href ?? SIGNUP_URL;
  const external = href.startsWith("http");
  return (
    <div className="flex flex-col gap-4 p-3">
      <p className="text-ds-heading-h5 text-grey-950 leading-[1.3]">{text}</p>
      <Button
        size="sm"
        className="w-fit"
        render={<Link href={href} external={external} />}
      >
        {label}
      </Button>
    </div>
  );
}
